import { ScreenContainer, ScreenTitleBar } from "../../../layout/ScreenCommon";
import { StepConfig } from "../../../../common/stepper/StepperHelpers";
import React, { useMemo } from "react";
import { DynamicStepper } from "../../../../common/stepper/StepperComponents";
import { DefaultQosLevelStep, ExcludeBootVolumesStep, IncludeAllSystemsStep, SelectSystemsStep } from "./MigrateOpsThinDataReportWizardSteps";
import { useNavigateToMigrateOpsRecipeWizard, ViewGeneratedConfigStep } from "../MigrateOpsWizardCommon";
import { Button } from "@mui/material";
import { useMigrateOpsThinDataReportWizardState } from "./MigrateOpsThinDataReportWizardState";
import { OperationRecipeID } from "gc-web-proto/galaxycompletepb/operationpb/operation_pb";
import { MigrateOpsWizardGetStartedStep } from "../MigrateOpsWizardGetStartedStep";
import { useTranslation } from "react-i18next";

// ======================
// CreateThinDataReportButton
// ======================

interface CreateThinDataReportButtonProps {
    compact?: boolean;
}

export const CreateThinDataReportButton: React.FC<CreateThinDataReportButtonProps> = (p) => {
    const { t } = useTranslation("migrateops");
    const goToOpWizard = useNavigateToMigrateOpsRecipeWizard();
    return (
        <Button
            variant={p.compact ? "text" : "contained"}
            size={p.compact ? "small" : "medium"}
            onClick={() => {
                goToOpWizard(OperationRecipeID.MIGRATEOPS_MIGRATION_SOURCE_ASSESSMENT_REPORT);
            }}
        >
            {p.compact ? t("wizard.thinDataReport.createShort") : t("wizard.thinDataReport.create")}
        </Button>
    );
};

export enum ThinDataReportWizardStepId {
    GET_STARTED = "get-started",
    INCLUDE_ALL_SYSTEMS = "include-all-systems",
    SELECT_SYSTEMS = "select-systems",
    EXCLUDE_BOOT_VOLUMES = "exclude-boot-volumes",
    DEFAULT_QOS_LEVEL = "default-qos-level",
    FINISH_UP = "finish-up",
}

// ======================
// MigrateOpsThinDataReportWizard
// ======================

interface MigrateOpsThinDataReportWizardProps {}

export const MigrateOpsThinDataReportWizard: React.FC<MigrateOpsThinDataReportWizardProps> = (p) => {
    const { t } = useTranslation("migrateops");
    const wizardState = useMigrateOpsThinDataReportWizardState();
    const { resetState } = wizardState;
    const includeAllSystems = wizardState.operationJson.config.migrateops_migration_source_assessment_report.include_all_systems_in_project;

    const stepConfigs = useMemo<StepConfig[]>(() => {
        const getStartedStep: StepConfig = {
            id: ThinDataReportWizardStepId.GET_STARTED,
            label: t("wizard.steps.gettingStarted"),
            renderer: () => <MigrateOpsWizardGetStartedStep id={ThinDataReportWizardStepId.GET_STARTED} />,
        };

        const includeAllsystemsStep: StepConfig = {
            id: ThinDataReportWizardStepId.INCLUDE_ALL_SYSTEMS,
            label: t("wizard.thinDataReport.steps.includeAllSystems"),
            renderer: () => <IncludeAllSystemsStep />,
        };

        const selectSystemsStep: StepConfig = {
            id: ThinDataReportWizardStepId.SELECT_SYSTEMS,
            label: t("wizard.thinDataReport.steps.selectSystems"),
            renderer: () => <SelectSystemsStep />,
        };

        const excludeBootVolumesStep: StepConfig = {
            id: ThinDataReportWizardStepId.EXCLUDE_BOOT_VOLUMES,
            label: t("wizard.thinDataReport.steps.excludeBootVolumes"),
            renderer: () => <ExcludeBootVolumesStep />,
        };

        const defaultQosLevelStep: StepConfig = {
            id: ThinDataReportWizardStepId.DEFAULT_QOS_LEVEL,
            label: t("wizard.thinDataReport.steps.defaultQosLevel"),
            renderer: () => <DefaultQosLevelStep />,
        };

        const finishUpStep: StepConfig = {
            id: ThinDataReportWizardStepId.FINISH_UP,
            label: t("wizard.steps.finishUp"),
            renderer: () => (
                <ViewGeneratedConfigStep
                    wizardState={useMigrateOpsThinDataReportWizardState}
                    submitButtonLabel={t("wizard.thinDataReport.create")}
                    resetState={resetState}
                    id={ThinDataReportWizardStepId.FINISH_UP}
                />
            ),
        };

        const configs = [getStartedStep, includeAllsystemsStep];
        if (includeAllSystems !== true) {
            configs.push(selectSystemsStep);
        }
        configs.push(excludeBootVolumesStep, defaultQosLevelStep, finishUpStep);
        return configs;
    }, [includeAllSystems, resetState, t]);

    return (
        <ScreenContainer>
            <ScreenTitleBar title={t("wizard.thinDataReport.title")} />
            <DynamicStepper
                displayType="progress"
                stepperProps={{
                    stepConfigs: stepConfigs,
                    startingStep: 0,
                    totalSteps: stepConfigs.length,
                }}
                orientation={"horizontal"}
                useTransitions
            />
        </ScreenContainer>
    );
};
